import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { BsArrowCounterclockwise } from 'react-icons/bs';

import type { ApiErrorResponse } from '@/models';

interface OTPVerificationViewProps {
  illustrationSrc: string;
  illustrationAlt?: string;

  title: React.ReactNode;
  description?: React.ReactNode;
  refCode?: string;

  expiresAt: Date | null;
  isOpen?: boolean;

  submitOTP: (code: string) => Promise<boolean>;
  resendOTP: () => Promise<void>;
  onLoading?: (isLoading: boolean) => void;
}

interface OTPCredential extends Credential {
  code: string;
}

interface OTPCredentialRequestOptions extends CredentialRequestOptions {
  otp: {
    transport: string[];
  };
  signal: AbortSignal;
}

const OTP_LENGTH = 6;

const OTP_ERROR_MESSAGE_KEYS = {
  INVALID_OTP: 'package.otp.invalidOtp',
  EXPIRED_OTP: 'package.otp.expiredOtp',
} as const;

const OTPVerificationView: React.FC<OTPVerificationViewProps> = ({
  illustrationSrc,
  illustrationAlt = 'Security Shield',
  title,
  description,
  refCode,
  expiresAt,
  isOpen = true,
  submitOTP,
  resendOTP,
  onLoading,
}) => {
  const { t } = useTranslation();

  const [otpDigits, setOtpDigits] = useState<string[]>(
    Array.from({ length: OTP_LENGTH }).map(() => ''),
  );
  const [validationError, setValidationError] = useState<string>('');
  const [timeLeftSeconds, setTimeLeftSeconds] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const isAutoSubmittingRef = useRef<boolean>(false);

  const isFormComplete = useMemo(() => {
    return otpDigits.every((digit) => digit !== '' && digit.length === 1);
  }, [otpDigits]);

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds
      .toString()
      .padStart(2, '0')}`;
  };

  const focusFirst = useCallback(() => {
    const firstInput = inputRefs.current[0];
    if (!firstInput) return;

    firstInput.focus({ preventScroll: true });
    firstInput.setSelectionRange?.(0, 1);
  }, []);

  const clearDigits = () => {
    setOtpDigits(Array.from({ length: OTP_LENGTH }).map(() => ''));
  };

  // Countdown timer
  useEffect(() => {
    if (!expiresAt) {
      setTimeLeftSeconds(0);
      return;
    }

    const updateTimeLeft = () => {
      const now = new Date();
      const diff = expiresAt.getTime() - now.getTime();

      if (diff <= 0) {
        setTimeLeftSeconds(0);
        return false;
      }

      setTimeLeftSeconds(Math.floor(diff / 1000));
      return true;
    };

    // Compute immediately to avoid brief enabled state for resend.
    if (!updateTimeLeft()) {
      return;
    }

    const interval = window.setInterval(() => {
      const shouldContinue = updateTimeLeft();
      if (!shouldContinue) {
        window.clearInterval(interval);
      }
    }, 1000);

    return () => window.clearInterval(interval);
  }, [expiresAt]);

  const updateDigit = (index: number, value: string) => {
    const nextDigits = [...otpDigits];
    nextDigits[index] = value;
    setOtpDigits(nextDigits);
  };

  const fillDigitsFrom = useCallback((startIndex: number, rawValue: string) => {
    const cleaned = rawValue.replace(/\D/g, '');
    if (!cleaned) return;

    let writeIndex = startIndex;

    setOtpDigits((prevDigits) => {
      const nextDigits = [...prevDigits];
      for (const char of cleaned) {
        if (writeIndex >= OTP_LENGTH) break;
        nextDigits[writeIndex] = char;
        writeIndex += 1;
      }
      return nextDigits;
    });

    const nextFocusIndex = Math.min(writeIndex, OTP_LENGTH - 1);
    inputRefs.current[nextFocusIndex]?.focus();
  }, []);

  const handleInputChange = (index: number, value: string) => {
    if (validationError) {
      setValidationError('');
    }

    if (value === '') {
      updateDigit(index, '');
      return;
    }

    const cleaned = value.replace(/\D/g, '');
    if (!cleaned) return;

    if (cleaned.length > 1) {
      fillDigitsFrom(index, cleaned);
      return;
    }

    updateDigit(index, cleaned);

    if (index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === 'Backspace') {
      if (!otpDigits[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
      return;
    }

    if (
      e.key === 'Tab' ||
      e.key === 'Delete' ||
      e.key === 'ArrowLeft' ||
      e.key === 'ArrowRight'
    ) {
      return;
    }

    if (!/^[0-9]$/.test(e.key)) {
      e.preventDefault();
    }
  };

  const handlePaste = (
    index: number,
    e: React.ClipboardEvent<HTMLInputElement>,
  ) => {
    const text = e.clipboardData.getData('text');
    if (!text) return;

    const cleaned = text.replace(/\D/g, '');
    if (!cleaned) return;

    e.preventDefault();
    fillDigitsFrom(index, cleaned);
  };

  const handleSubmitOTP = useCallback(async () => {
    const otpCode = otpDigits.join('');
    if (otpCode.length !== OTP_LENGTH) return;

    try {
      setIsLoading(true);
      onLoading?.(true);
      setValidationError('');

      const isValid = await submitOTP(otpCode);

      if (isValid) {
        clearDigits();
        return;
      }

      setValidationError(t('package.otp.invalidOtp'));
      clearDigits();
      focusFirst();
    } catch (error: unknown) {
      console.error('OTP validation failed:', error);
      const errorCode = (error as { response?: { data?: ApiErrorResponse } })
        .response?.data?.errorCode;
      const errorKey =
        errorCode && errorCode in OTP_ERROR_MESSAGE_KEYS
          ? OTP_ERROR_MESSAGE_KEYS[
              errorCode as keyof typeof OTP_ERROR_MESSAGE_KEYS
            ]
          : OTP_ERROR_MESSAGE_KEYS.INVALID_OTP;

      setValidationError(t(errorKey));
      clearDigits();
      focusFirst();
    } finally {
      setIsLoading(false);
      onLoading?.(false);
    }
  }, [focusFirst, onLoading, otpDigits, submitOTP, t]);

  // Auto-submit when all digits are entered
  useEffect(() => {
    if (!isOpen) return;
    if (!isFormComplete || isLoading || isAutoSubmittingRef.current) return;

    isAutoSubmittingRef.current = true;
    handleSubmitOTP().finally(() => {
      // Keep it true if the view has since been closed.
      if (isOpen) {
        isAutoSubmittingRef.current = false;
      }
    });
  }, [handleSubmitOTP, isFormComplete, isLoading, isOpen]);

  // Reset auto-submit guard when (re)opened.
  useEffect(() => {
    if (isOpen) {
      isAutoSubmittingRef.current = false;
    } else {
      isAutoSubmittingRef.current = true;
    }
  }, [isOpen]);

  // Try native-like SMS OTP autofill on supported browsers (WebOTP).
  useEffect(() => {
    if (!isOpen) return;

    const hasCredentialsApi =
      typeof window !== 'undefined' && 'credentials' in navigator;
    if (!hasCredentialsApi) return;

    const abortController = new AbortController();

    const requestOtp = async () => {
      try {
        const credential = (await navigator.credentials.get({
          otp: { transport: ['sms'] },
          signal: abortController.signal,
        } as OTPCredentialRequestOptions)) as OTPCredential | null;

        const code = credential?.code?.replace(/\D/g, '') ?? '';
        if (!code) return;

        fillDigitsFrom(0, code);
      } catch (error) {
        // Keep manual input as fallback when WebOTP is unavailable.
        console.debug('WebOTP not available or cancelled:', error);
      }
    };

    void requestOtp();

    return () => {
      abortController.abort();
    };
  }, [fillDigitsFrom, isOpen]);

  // Auto-focus first OTP box on open so users can type immediately.
  useEffect(() => {
    if (!isOpen) return;

    const timeoutId = window.setTimeout(() => {
      focusFirst();
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [focusFirst, isOpen]);

  const handleResend = async () => {
    try {
      setIsLoading(true);
      onLoading?.(true);
      await resendOTP();
      setValidationError('');
      clearDigits();
      focusFirst();
    } catch (error) {
      console.error('Failed to resend OTP:', error);
      setValidationError(t('package.otp.failedToSend'));
    } finally {
      setIsLoading(false);
      onLoading?.(false);
    }
  };

  const isResendDisabled = timeLeftSeconds > 0 || isLoading;

  return (
    <div>
      {/* Illustration */}
      <div className="mb-6 flex justify-center">
        <img
          src={illustrationSrc}
          alt={illustrationAlt}
          className="h-[200px] w-[200px] object-contain"
          draggable={false}
        />
      </div>

      {/* Title + meta */}
      <div className="mb-6 flex flex-col items-center gap-2 text-center">
        <h2
          id="otp-title"
          className="max-w-[360px] text-lg leading-7 font-semibold text-black"
        >
          {title}
        </h2>
        {description && (
          <p className="max-w-[360px] leading-6 text-gray-600">{description}</p>
        )}
        {refCode && (
          <p className="leading-6 text-gray-500">
            {t('package.otp.refCode', { ref: refCode })}
          </p>
        )}
      </div>

      {/* OTP Input fields */}
      <div className="mb-6">
        <div
          className="mb-4 flex justify-center gap-2 sm:gap-3"
          aria-label={t('package.otp.enter6DigitsAria')}
        >
          {Array.from({ length: OTP_LENGTH }).map((_, index) => (
            <input
              key={index}
              type="text"
              inputMode="numeric"
              pattern="[0-9]"
              aria-label={`Digit ${index + 1}`}
              maxLength={index === 0 ? OTP_LENGTH : 1}
              value={otpDigits[index] ?? ''}
              onChange={(e) => handleInputChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={(e) => handlePaste(index, e)}
              autoFocus={index === 0}
              autoComplete={index === 0 ? 'one-time-code' : 'off'}
              name={index === 0 ? 'otp' : `otp-${index + 1}`}
              className={`focus:black h-12 w-12 rounded-lg border text-center text-lg font-semibold transition-colors focus:ring-1 focus:outline-none sm:h-12 sm:w-12 ${
                validationError ? 'border-red-500' : 'border-gray-300'
              }`}
              ref={(el) => {
                inputRefs.current[index] = el;
              }}
              disabled={isLoading}
            />
          ))}
        </div>

        {validationError && (
          <p className="mb-4 text-center text-sm text-red-500" role="alert">
            {validationError}
          </p>
        )}

        <div className="flex items-center justify-between">
          <div className="text-error text-lg leading-7 font-normal">
            {timeLeftSeconds > 0 ? formatTime(timeLeftSeconds) : '00:00'}
          </div>
          <button
            type="button"
            onClick={handleResend}
            disabled={isResendDisabled}
            className={`text-primary flex items-center gap-1 text-lg leading-7 font-semibold sm:gap-2 ${
              isResendDisabled
                ? 'cursor-not-allowed opacity-50'
                : 'hover:opacity-80'
            }`}
          >
            <BsArrowCounterclockwise className="size-4" />
            {t('package.otp.resend')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OTPVerificationView;
